import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, Wifi, WifiOff, ArrowLeft, Lock, CheckCircle, 
  AlertCircle, Save, Mail, Loader2, Database, 
  Users, Calendar, Settings, Eye, Edit, Clock, X, Info,
  Zap, RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

// Hooks
import { useIntegrationsByPracticeId } from '../../hooks/Integration/useIntegration';
import useCreateIntegration from '../../hooks/Integration/useCreateIntegration';
import useUpdateIntegration from '../../hooks/Integration/useUpdateIntegration';
import { useScraperOperations } from '../../hooks/Scraper/useScraperHooks';
import { RootState } from '../../redux';

// Types
interface IntegrationData {
  id?: string;
  name: string;
  integration_type: string;
  practice_id: string;
  status: 'active' | 'inactive' | 'pending' | 'error';
  auth: {
    username: string;
    password: string;
    api_key?: string;
  };
  config: {
    sync_frequency: string;
    last_sync?: string;
    auto_sync: boolean;
  };
}

interface FormInputProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  helpText?: string;
}

// Animation Variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 }
};

const slideInRight = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
};

// Progress Steps Component
const ProgressSteps: React.FC<{
  currentStep: number;
  steps: Array<{
    id: string;
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    status: 'pending' | 'active' | 'completed' | 'error';
  }>;
}> = ({ currentStep, steps }) => (
  <div className="space-y-3">
    {steps.map((step, index) => {
      const Icon = step.icon;
      const isActive = step.status === 'active';
      const isCompleted = step.status === 'completed';
      const isError = step.status === 'error';
      
      return (
        <motion.div
          key={step.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
          className={`relative flex items-center space-x-4 p-4 rounded-xl border-2 transition-all duration-300 ${
            isActive 
              ? 'border-blue-400 bg-gradient-to-r from-blue-50 to-blue-100/80 shadow-lg scale-105' 
              : isCompleted 
                ? 'border-green-300 bg-gradient-to-r from-green-50 to-green-100/80' 
                : isError 
                  ? 'border-red-300 bg-gradient-to-r from-red-50 to-red-100/80'
                  : 'border-gray-200 bg-gray-50/50'
          }`}
        >
          {/* Progress line */}
          {index < steps.length - 1 && (
            <div className={`absolute left-9 top-full w-0.5 h-3 ${
              isCompleted ? 'bg-green-400' : 'bg-gray-300'
            }`} />
          )}
          
          <motion.div 
            animate={isActive ? { scale: [1, 1.1, 1] } : {}}
            transition={{ repeat: isActive ? Infinity : 0, duration: 2 }}
            className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center shadow-md ${
              isActive 
                ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' 
                : isCompleted 
                  ? 'bg-gradient-to-br from-green-500 to-green-600 text-white' 
                  : isError 
                    ? 'bg-gradient-to-br from-red-500 to-red-600 text-white'
                    : 'bg-white border-2 border-gray-300 text-gray-400'
            }`}
          >
            {isActive ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : isCompleted ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <CheckCircle className="w-6 h-6" />
              </motion.div>
            ) : isError ? (
              <AlertCircle className="w-6 h-6" />
            ) : (
              <Icon className="w-6 h-6" />
            )}
          </motion.div>
          
          <div className="flex-1 min-w-0">
            <h4 className={`font-semibold text-sm ${
              isActive 
                ? 'text-blue-900' 
                : isCompleted 
                  ? 'text-green-900' 
                  : isError 
                    ? 'text-red-900'
                    : 'text-gray-600'
            }`}>
              {step.title}
            </h4>
            <p className={`text-xs mt-0.5 ${
              isActive 
                ? 'text-blue-700' 
                : isCompleted 
                  ? 'text-green-700' 
                  : isError 
                    ? 'text-red-700'
                    : 'text-gray-500'
            }`}>
              {step.description}
            </p>
          </div>
          
          {isActive && (
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="flex-shrink-0"
            >
              <div className="w-2 h-2 rounded-full bg-blue-500" />
            </motion.div>
          )}
        </motion.div>
      );
    })}
  </div>
);

// Form Input Component
const FormInput: React.FC<FormInputProps> = ({ 
  icon: Icon, 
  label, 
  type = "text", 
  value, 
  onChange, 
  placeholder, 
  required = false,
  error,
  disabled = false,
  helpText
}) => (
  <motion.div 
    variants={fadeInUp}
    className="space-y-2"
  >
    <label className="flex items-center text-sm font-semibold text-gray-700">
      <Icon className="w-4 h-4 mr-2 text-indigo-600" />
      {label} {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
          error
            ? "border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50/50"
            : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20 hover:border-gray-300 bg-white"
        } placeholder-gray-400 focus:outline-none focus:ring-4 ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
        disabled={disabled}
      />
    </div>
    <AnimatePresence mode="wait">
      {error && (
        <motion.p 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="text-sm text-red-600 flex items-center"
        >
          <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
          {error}
        </motion.p>
      )}
      {!error && helpText && (
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-xs text-gray-500 flex items-center"
        >
          <Info className="w-3 h-3 mr-1 flex-shrink-0" />
          {helpText}
        </motion.p>
      )}
    </AnimatePresence>
  </motion.div>
);

// Integration Table Component
const IntegrationTable: React.FC<{
  integration: {
    id?: string;
    name?: string;
    integrationName?: string;
    integrationType?: string;
    practiceId?: string;
    connectionStatus?: string;
    status?: string;
    username?: string;
    createdAt?: string;
  };
  onViewDetails: () => void;
  onEdit: () => void;
}> = ({ integration, onViewDetails, onEdit }) => {
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'connected':
        return 'green';
      case 'inactive':
      case 'disconnected':
        return 'gray';
      case 'pending':
        return 'yellow';
      case 'error':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'connected':
        return CheckCircle;
      case 'inactive':
      case 'disconnected':
        return WifiOff;
      case 'pending':
        return Clock;
      case 'error':
        return AlertCircle;
      default:
        return WifiOff;
    }
  };

  const status = integration.connectionStatus || integration.status || 'inactive';
  const statusColor = getStatusColor(status);
  const StatusIcon = getStatusIcon(status);

  return (
    <motion.div 
      variants={scaleIn}
      initial="initial"
      animate="animate"
      className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-xl p-8"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Existing Integration</h2>
          <p className="text-gray-600">
            Manage your SimplePractice integration configuration.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onViewDetails}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
          >
            <Eye className="h-4 w-4" />
            <span>View Details</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onEdit}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md"
          >
            <Edit className="h-4 w-4" />
            <span>Edit</span>
          </motion.button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Integration
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Practice ID
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Username
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <motion.tr 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="hover:bg-blue-50/50 transition-colors"
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-sm">
                      <Database className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-semibold text-gray-900">
                      {integration.integrationName || integration.name || 'SimplePractice Integration'}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {integration.integrationType || 'simple_practice'}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-${statusColor}-100 text-${statusColor}-800 border border-${statusColor}-200`}>
                  <StatusIcon className="h-3 w-3 mr-1.5" />
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                {integration.practiceId || 'N/A'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {integration.username || 'N/A'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {integration.createdAt 
                  ? new Date(integration.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })
                  : 'N/A'
                }
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={onViewDetails}
                    className="text-blue-600 hover:text-blue-900 flex items-center space-x-1 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View</span>
                  </button>
                  <button
                    onClick={onEdit}
                    className="text-green-600 hover:text-green-900 flex items-center space-x-1 transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit</span>
                  </button>
                </div>
              </td>
            </motion.tr>
          </tbody>
        </table>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200"
      >
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <CheckCircle className="h-5 w-5 text-blue-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-semibold text-blue-900">
              Integration Active
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Your SimplePractice integration is configured and ready to use. Click "View Details" to see more information or "Edit" to modify the configuration.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component
const SimplePracticeIntegrationForm: React.FC = () => {
  const navigate = useNavigate();
  
  // Get user data from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const practiceId = user?.["custom:id_practice"] || user?.practice_id;
  
  // Check if we're in edit mode
  const urlParams = new URLSearchParams(window.location.search);
  const integrationId = urlParams.get('id');
  const isEditMode = !!integrationId;
  
  // Hooks
  const { integration: existingIntegration, loading } = useIntegrationsByPracticeId(practiceId || '');
  const { createIntegrationAction } = useCreateIntegration();
  const { updateIntegrationAction } = useUpdateIntegration();
  const { createIntegrationWithScraper: scraperState } = useScraperOperations();
  
  // Redux state
  const integrationLoading = useSelector((state: RootState) => state.integration.isLoading);
  const integrationError = useSelector((state: RootState) => state.integration.error);

  // State
  const [integrationForm, setIntegrationForm] = useState<IntegrationData>({
    name: 'SimplePractice',
    integration_type: 'simplepractice',
    practice_id: practiceId || '',
    status: 'inactive',
    auth: {
      username: '',
      password: '',
    },
    config: {
      sync_frequency: 'hourly',
      auto_sync: true
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showDetails, setShowDetails] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  // Progress steps
  const [progressSteps, setProgressSteps] = useState([
    {
      id: 'validation',
      title: 'Validating Credentials',
      description: 'Checking SimplePractice connection...',
      icon: Shield,
      status: 'pending' as 'pending' | 'active' | 'completed' | 'error'
    },
    {
      id: 'integration',
      title: isEditMode ? 'Updating Integration' : 'Creating Integration',
      description: isEditMode ? 'Updating SimplePractice integration...' : 'Setting up SimplePractice integration...',
      icon: Settings,
      status: 'pending' as 'pending' | 'active' | 'completed' | 'error'
    },
    {
      id: 'scraper',
      title: 'Running Initial Sync',
      description: 'Syncing clinician data from SimplePractice...',
      icon: Database,
      status: 'pending' as 'pending' | 'active' | 'completed' | 'error'
    },
    {
      id: 'clinicians',
      title: 'Processing Clinicians',
      description: 'Importing clinician profiles and availability...',
      icon: Users,
      status: 'pending' as 'pending' | 'active' | 'completed' | 'error'
    },
    {
      id: 'calendar',
      title: 'Setting Up Calendar',
      description: 'Configuring calendar sync and availability...',
      icon: Calendar,
      status: 'pending' as 'pending' | 'active' | 'completed' | 'error'
    }
  ]);

  // Handle existing integration display
  useEffect(() => {
    if (existingIntegration && !loading && !isEditMode) {
      navigate(`/practice/integration/simplepractice/details?id=${existingIntegration.id}`);
    }
  }, [existingIntegration, loading, isEditMode, navigate]);

  // Update form with existing integration data
  useEffect(() => {
    if (existingIntegration && !loading && isEditMode) {
      setIntegrationForm({
        id: existingIntegration.id,
        name: existingIntegration.integrationName || existingIntegration.name || 'SimplePractice',
        integration_type: existingIntegration.integrationType || 'simplepractice',
        practice_id: existingIntegration.practiceId || practiceId || '',
        status: existingIntegration.connectionStatus === 'active' ? 'active' : 'inactive',
        auth: {
          username: existingIntegration.username || '',
          password: existingIntegration.password || '',
        },
        config: {
          sync_frequency: 'hourly',
          auto_sync: true
        }
      });
    }
  }, [existingIntegration, loading, practiceId, isEditMode]);

  // Handle progress updates
  useEffect(() => {
    if (showProgress) {
      const timers: NodeJS.Timeout[] = [];
      
      // Step 1: Validation (immediate)
      setCurrentStep(0);
      updateStepStatus(0, 'active');
      
      // Step 2: Integration creation
      timers.push(setTimeout(() => {
        updateStepStatus(0, 'completed');
        setCurrentStep(1);
        updateStepStatus(1, 'active');
      }, 1000));
      
      // Step 3: Scraper running
      timers.push(setTimeout(() => {
        updateStepStatus(1, 'completed');
        setCurrentStep(2);
        updateStepStatus(2, 'active');
      }, 2000));
      
      // Step 4: Clinicians processing
      timers.push(setTimeout(() => {
        updateStepStatus(2, 'completed');
        setCurrentStep(3);
        updateStepStatus(3, 'active');
      }, 3000));
      
      // Step 5: Calendar setup
      timers.push(setTimeout(() => {
        updateStepStatus(3, 'completed');
        setCurrentStep(4);
        updateStepStatus(4, 'active');
      }, 4000));

      // The final step is completed by handleSubmit only once the API call
      // actually succeeds — never on a timer.
      return () => timers.forEach(timer => clearTimeout(timer));
    }
  }, [showProgress]);

  const updateStepStatus = useCallback((stepIndex: number, status: 'pending' | 'active' | 'completed' | 'error') => {
    setProgressSteps(prev => 
      prev.map((step, index) => 
        index === stepIndex ? { ...step, status } : step
      )
    );
  }, []);

  // Validation
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    
    if (!integrationForm.auth.username.trim()) {
      errors.username = 'Username is required';
    } else if (integrationForm.auth.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    
    if (!integrationForm.auth.password.trim()) {
      errors.password = 'Password is required';
    } else if (integrationForm.auth.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [integrationForm.auth.username, integrationForm.auth.password]);

  // Computed form validity
  const isFormValid = integrationForm.auth.username.trim().length >= 3 && 
                      integrationForm.auth.password.trim().length >= 6;

  // Handlers
  const handleFormChange = useCallback((field: string, value: string | boolean) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setIntegrationForm(prev => {
        const parentObj = prev[parent as keyof IntegrationData] as Record<string, unknown>;
        return {
          ...prev,
          [parent]: {
            ...parentObj,
            [child]: value
          }
        };
      });
    } else {
      setIntegrationForm(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear validation error for this field
    if (validationErrors[field.split('.').pop() || field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field.split('.').pop() || field];
        return newErrors;
      });
    }
  }, [validationErrors]);

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Small delay before showing progress for smoother transition
      await new Promise(resolve => setTimeout(resolve, 300));
      setShowProgress(true);

      const integrationToSave = {
        ...(isEditMode && integrationId ? { id: integrationId } : {}),
        name: 'SimplePractice Integration',
        practiceId: integrationForm.practice_id,
        integrationType: 'simple_practice',
        integrationName: 'SimplePractice Integration',
        username: integrationForm.auth.username,
        password: integrationForm.auth.password,
        connectionStatus: 'connected',
      };
      
      // Start the actual API call
      const savePromise = isEditMode 
        ? updateIntegrationAction(integrationToSave)
        : createIntegrationAction(integrationToSave);

      // Wait for both the API call and minimum progress display time
      await Promise.all([
        savePromise,
        new Promise(resolve => setTimeout(resolve, 5000)) // Min 5s for progress animation
      ]);

      // The API call really succeeded — now the flow can complete
      updateStepStatus(4, 'completed');
      toast.success(isEditMode ? 'Integration updated successfully!' : 'Integration created successfully!');

      // Update form status
      setIntegrationForm(prev => ({
        ...prev,
        status: 'active', 
        config: {
          ...prev.config,
          last_sync: new Date().toISOString()
        }
      }));

      // Smooth transition to success view
      await new Promise(resolve => setTimeout(resolve, 500));
      setShowProgress(false);
      setShowDetails(true);
      
      // Wait for success screen, then navigate
      setTimeout(() => {
        const integrationIdToUse = isEditMode && integrationId 
          ? integrationId 
          : existingIntegration?.id;
        
        if (integrationIdToUse) {
          navigate(`/practice/integration/simplepractice/details?id=${integrationIdToUse}`);
        } else {
          setTimeout(() => {
            if (existingIntegration?.id) {
              navigate(`/practice/integration/simplepractice/details?id=${existingIntegration.id}`);
            } else {
              navigate('/practice/integration/simplepractice/details');
            }
          }, 500);
        }
      }, 2000); // Show success for 2 seconds

    } catch (error) {
      console.error('Integration save error:', error);
      const message = error instanceof Error ? error.message
        : typeof error === 'string' ? error
        : 'An unexpected error occurred';
      setSubmitError(message);
      toast.error(isEditMode ? `Failed to update integration: ${message}` : `Failed to create integration: ${message}`);
      setShowProgress(false);
      setIsSubmitting(false);

      // Update progress to show error
      setProgressSteps(prev =>
        prev.map((step, index) =>
          index === currentStep ? { ...step, status: 'error' } : step
        )
      );
    }
  };

  // If showing progress, render progress view
  if (showProgress) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
      >
        <div className="p-8">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl p-8"
            >
              <div className="text-center mb-8">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.6, delay: 0.2 }}
                  className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                >
                  <Loader2 className="w-10 h-10 text-white animate-spin" />
                </motion.div>
                <motion.h2 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-3xl font-bold text-gray-900 mb-3"
                >
                  Setting Up Your Integration
                </motion.h2>
                <motion.p 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-gray-600 text-lg"
                >
                  We're configuring your SimplePractice integration. This may take a few moments...
                </motion.p>
              </div>
              
              <ProgressSteps currentStep={currentStep} steps={progressSteps} />
              
              <div className="mt-8 text-center space-y-3">
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
                  <motion.div 
                    className="bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-600 h-3 rounded-full shadow-md"
                    initial={{ width: 0 }}
                    animate={{ width: `${((currentStep + 1) / progressSteps.length) * 100}%` }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  />
                </div>
                <motion.p 
                  key={currentStep}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm font-medium text-gray-600"
                >
                  Step {currentStep + 1} of {progressSteps.length}
                </motion.p>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-xs text-gray-500"
                >
                  Please don't close this window
                </motion.p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }

  // If showing details, render details view
  if (showDetails) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
      >
        <div className="p-8">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl p-12"
            >
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.8, delay: 0.2 }}
                  className="relative mx-auto w-24 h-24 mb-6"
                >
                  <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-75" />
                  <div className="relative bg-gradient-to-br from-green-400 to-green-600 rounded-full w-24 h-24 flex items-center justify-center shadow-lg">
                    <CheckCircle className="w-12 h-12 text-white" />
                  </div>
                </motion.div>
                
                <motion.h2 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-3xl font-bold text-gray-900 mb-3"
                >
                  Integration Successful!
                </motion.h2>
               
                <motion.p 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-gray-600 text-lg mb-8"
                >
                  Your SimplePractice integration has been set up successfully.
                </motion.p>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="flex items-center justify-center space-x-3 mb-8"
                >
                  <div className="flex items-center space-x-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                    <Zap className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Connected</span>
                  </div>
                  <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <RefreshCw className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Ready to Sync</span>
                  </div>
                </motion.div>

                <motion.button 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/practice/integrations')}
                  className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl font-medium"
                >
                  Back to Integrations
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="space-y-6"
          > 
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <motion.button 
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => navigate('/practice/integrations')}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/60 transition-all duration-200 shadow-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </motion.button>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-1">
                      SimplePractice Integration
                    </h1>
                    <p className="text-gray-600">
                      {isEditMode ? 'Update your integration settings' : 'Connect your SimplePractice account'}
                    </p>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="hidden md:flex items-center space-x-3"
                >
                  <div className="px-4 py-2 bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Secure</span>
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center space-x-2">
                      <Wifi className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Real-time Sync</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
            
            {/* Loading State */}
            {/* <AnimatePresence>
              {loading && (
                <motion.div 
                  variants={fadeInUp}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="bg-yellow-50/80 border border-yellow-200/50 rounded-xl p-5 backdrop-blur-sm shadow-sm"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 border-2 border-yellow-400 border-t-yellow-600 rounded-full animate-spin"></div>
                    <span className="text-yellow-800 font-medium">Loading integration data...</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence> */}
            
            {/* Error State */}
            <AnimatePresence>
              {(submitError || integrationError || scraperState.error) && (
                <motion.div
                  variants={fadeInUp}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="bg-red-50/80 border border-red-200/50 rounded-xl p-5 backdrop-blur-sm shadow-sm"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-semibold text-red-900 mb-1">Error</h3>
                        <p className="text-sm text-red-800">
                          {submitError || integrationError || scraperState.error}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSubmitError(null)}
                      className="text-red-400 hover:text-red-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Show existing integration table or form */}
            {!loading && existingIntegration && 
             existingIntegration.id && 
             existingIntegration.practiceId && 
             existingIntegration.username && 
             existingIntegration.id !== 'undefined' && 
             existingIntegration.id !== '' && 
             !isEditMode && 
             !showProgress ? (
              <IntegrationTable 
                integration={existingIntegration} 
                onViewDetails={() => navigate(`/practice/integration/simplepractice/details?id=${existingIntegration.id}`)}
                onEdit={() => navigate(`/practice/integration/simplepractice/form?id=${existingIntegration.id}`)}
              />
            ) : (
              <>
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  {/* Main Form Section */}
                  <motion.div 
                    variants={scaleIn}
                    initial="initial"
                    animate="animate"
                    className="xl:col-span-2"
                  >
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-xl p-8">
                      <div className="space-y-8">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3 shadow-md">
                              <Settings className="w-5 h-5 text-white" />
                            </div>
                            {isEditMode ? 'Update Integration' : 'Create New Integration'}
                          </h3>
                          <p className="text-gray-600 ml-13">
                            {isEditMode 
                              ? 'Update your SimplePractice integration settings.'
                              : 'Enter your SimplePractice credentials to establish the connection.'
                            }
                          </p>
                        </div>

                        {/* Form Fields */}
                        <motion.div 
                          initial="initial"
                          animate="animate"
                          variants={{
                            initial: {},
                            animate: { transition: { staggerChildren: 0.1 } }
                          }}
                          className="space-y-6"
                        >
                          <FormInput
                            icon={Mail}
                            label="SimplePractice Username"
                            value={integrationForm.auth.username}
                            onChange={(e) => handleFormChange('auth.username', e.target.value)}
                            placeholder="Enter your SimplePractice username"
                            required
                            error={validationErrors.username}
                            disabled={isSubmitting || integrationLoading || scraperState.loading}
                            helpText="Your SimplePractice account username or email"
                          />

                          <div className="relative">
                            <FormInput
                              icon={Lock}
                              label="SimplePractice Password"
                              type={showPassword ? "text" : "password"}
                              value={integrationForm.auth.password}
                              onChange={(e) => handleFormChange('auth.password', e.target.value)}
                              placeholder="Enter your SimplePractice password"
                              required
                              error={validationErrors.password}
                              disabled={isSubmitting || integrationLoading || scraperState.loading}
                              helpText="Your password is encrypted and stored securely"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-4 top-10 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </motion.div>

                        {/* Action Buttons */}
                        <motion.div 
                          variants={fadeInUp}
                          className="flex flex-col sm:flex-row gap-4 pt-4"
                        >
                          <motion.button
                            whileHover={isFormValid && !isSubmitting && !integrationLoading && !scraperState.loading ? { scale: 1.02 } : {}}
                            whileTap={isFormValid && !isSubmitting && !integrationLoading && !scraperState.loading ? { scale: 0.98 } : {}}
                            onClick={handleSubmit}
                            disabled={!isFormValid || isSubmitting || integrationLoading || scraperState.loading}
                            className={`flex-1 px-6 py-4 rounded-xl font-semibold transition-all duration-200 ${
                              isFormValid && !isSubmitting && !integrationLoading && !scraperState.loading
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {(isSubmitting || integrationLoading || scraperState.loading) ? (
                              <div className="flex items-center justify-center space-x-2">
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                <span>{isEditMode ? 'Updating Integration...' : 'Creating Integration...'}</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center space-x-2">
                                <Save className="h-5 w-5" />
                                <span>{isEditMode ? 'Update Integration' : 'Create Integration'}</span>
                              </div>
                            )}
                          </motion.button>
                        </motion.div>

                        {/* Form validation info */}
                        {!isFormValid && (integrationForm.auth.username || integrationForm.auth.password) && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-4 bg-blue-50 border border-blue-200 rounded-lg"
                          >
                            <div className="flex items-start space-x-3">
                              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                              <div className="text-sm text-blue-800">
                                <p className="font-medium mb-1">Complete all required fields:</p>
                                <ul className="list-disc list-inside space-y-1 text-blue-700">
                                  {integrationForm.auth.username.length < 3 && <li>Username must be at least 3 characters</li>}
                                  {integrationForm.auth.password.length < 6 && <li>Password must be at least 6 characters</li>}
                                </ul>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </motion.div>

                  {/* Info Cards */}
                  <motion.div 
                    variants={slideInRight}
                    initial="initial"
                    animate="animate"
                    className="space-y-6"
                  >
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-lg p-6">
                      <div className="flex items-center space-x-3 mb-5">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                          <Wifi className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">
                          Integration Benefits
                        </h3>
                      </div>
                      <div className="space-y-3 text-sm">
                        {[
                          { icon: CheckCircle, text: 'Sync clinician data automatically', color: 'green' },
                          { icon: Calendar, text: 'Real-time calendar updates', color: 'blue' },
                          { icon: Shield, text: 'Secure credential storage', color: 'purple' },
                          { icon: Zap, text: 'Instant availability updates', color: 'yellow' }
                        ].map((item, index) => {
                          const Icon = item.icon;
                          return (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.1 * index }}
                              className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <Icon className={`w-5 h-5 text-${item.color}-500 flex-shrink-0 mt-0.5`} />
                              <span className="text-gray-700">{item.text}</span>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200 shadow-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-md">
                          <Shield className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">
                          Security & Privacy
                        </h3>
                      </div>
                      <div className="space-y-3 text-sm text-gray-700">
                        <p className="flex items-start">
                          <Lock className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          Your SimplePractice credentials are encrypted using industry-standard AES-256 encryption.
                        </p>
                        <p className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          We only use your credentials to sync data and never share them with third parties.
                        </p>
                        <p className="flex items-start">
                          <Shield className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          All data transmission is secured with SSL/TLS protocols.
                        </p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 shadow-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md">
                          <Info className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">
                          Need Help?
                        </h3>
                      </div>
                      <p className="text-sm text-gray-700 mb-4">
                        If you encounter any issues during integration, our support team is here to help.
                      </p>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="w-full px-4 py-2 bg-white border-2 border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-medium shadow-sm"
                      >
                        Contact Support
                      </motion.button>
                    </div>
                  </motion.div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SimplePracticeIntegrationForm;